import type { AccountPrice, Prices } from "@b2bportal/core-utilities";
import type {
  FiatPrice,
  PaymentLineItem,
  Pricing,
  PricingV1,
  RoomProduct,
} from "@b2bportal/lodging-api";
import type { Amount } from "@hopper-b2b/types";
import type { PaymentLineItemValue } from "@/overrides/core-hotels/HotelReservationPage/hooks/useHotelReservationPayment";
import { extractLoyaltyPointsPayment } from "@/utils/trips";

const POINTS_KEY = "Points" as const;

export const roundUpFiatPrice = (fiatPrice: FiatPrice): FiatPrice => {
  const value = Math.ceil(fiatPrice.value);
  return {
    ...fiatPrice,
    value,
  };
};

export const roundPriceToScale = (price: number) => {
  return Math.round(price * 100) / 100;
};

export const roundUpPriceToScale = (price: number) => {
  return Math.ceil(price * 100) / 100;
};

type PriceSpecificKey = typeof POINTS_KEY;

export const roundAccountSpecificToScale = (value: number) => {
  return Math.round(value);
};

export const getAccountSpecificPrice = (
  price: Prices,
  priceKey: PriceSpecificKey
): AccountPrice | undefined => {
  return price.accountSpecific[priceKey];
};
export const getPointsFromPrice = (price: Prices): AccountPrice | undefined => {
  return getAccountSpecificPrice(price, POINTS_KEY);
};

export const getPointsAmountFromPrice = (price: Prices): number => {
  return getPointsFromPrice(price)?.value ?? 0;
};

export const subtractAccountSpecificPrice = (
  price1: Prices,
  price2: Prices,
  priceKey: PriceSpecificKey
): AccountPrice | undefined => {
  const accountPrice1 = getAccountSpecificPrice(price1, priceKey);
  const accountPrice2 = getAccountSpecificPrice(price2, priceKey);

  if (!accountPrice1) {
    return;
  }
  if (!accountPrice2) {
    return accountPrice1;
  }

  if (accountPrice1.currency !== accountPrice2.currency) {
    console.error(`Currency mismatch in account specific price subtraction`);
    return;
  }

  return {
    currency: accountPrice1.currency,
    value: accountPrice1.value - accountPrice2.value,
    currencyDescription: accountPrice1.currencyDescription,
  };
};

export const divideAccountSpecificPrice = (
  price: Prices,
  divisor: number,
  priceKey: PriceSpecificKey
): AccountPrice | undefined => {
  const accountPrice = getAccountSpecificPrice(price, priceKey);
  if (!accountPrice) {
    return;
  }
  return {
    currency: accountPrice.currency,
    value: roundAccountSpecificToScale(accountPrice.value / divisor),
    currencyDescription: accountPrice.currencyDescription,
  };
};

const fiatPriceZero = ({
  currencyCode,
  currencySymbol,
}: FiatPrice): FiatPrice => ({
  value: 0,
  currencyCode,
  currencySymbol,
});

const pricesZero = (baseFiat: FiatPrice): Prices => ({
  fiat: fiatPriceZero(baseFiat),
  accountSpecific: {},
});

/**
 * Sums multiple instances of FiatPrice
 * Ignores null or undefined values. If all values are undefined, will return undefined.
 * Will return undefined and log an error if any of the prices have different currency codes
 */
export const sumFiatPrices = (
  priceList: (FiatPrice | undefined)[]
): undefined | FiatPrice => {
  const filtered = priceList.filter((price) => price != null) as FiatPrice[];
  if (filtered.length === 0) {
    return;
  }
  const currencyCode = filtered[0]?.currencyCode;
  const currencySymbol = filtered[0]?.currencySymbol;
  if (filtered.some((x) => x.currencyCode !== currencyCode)) {
    console.error(
      `Currency mismatch in fiat price sum: ${priceList
        .map((x) => x.currencyCode)
        .join(", ")}`
    );
    return;
  }

  const value = filtered.reduce((acc, price) => acc + price.value, 0);
  return {
    value: roundPriceToScale(value),
    currencyCode,
    currencySymbol,
  };
};

export const subtractFiatPrice = (
  price1: FiatPrice,
  price2: FiatPrice
): undefined | FiatPrice => {
  if (price1.currencyCode !== price2.currencyCode) {
    throw new Error(
      `Currency mismatch in fiat price subtraction: ${price1.currencyCode} and ${price2.currencyCode} are different.`
    );
  }

  return {
    currencyCode: price1.currencyCode,
    currencySymbol: price1.currencySymbol,
    value: roundPriceToScale(price1.value - price2.value),
  };
};

const subtractFiatAndLodgingAmount = (
  price1: FiatPrice,
  price2?: Amount
): FiatPrice | undefined => {
  if (!price2) {
    return price1;
  }
  if (price1.currencyCode === price2.currency) {
    return {
      value: price1.value - price2.amount,
      currencyCode: price1.currencyCode,
      currencySymbol: price1.currencySymbol,
    };
  } else {
    console.error(
      `Currency mismatch in points payment: ${price1.currencyCode} and ${price2.currency} are different.`
    );
  }
};

export const subtractPrice = (price1: Prices, price2: Prices): Prices => {
  const pointsDifference = subtractAccountSpecificPrice(
    price1,
    price2,
    POINTS_KEY
  );
  const accountSpecific = pointsDifference
    ? { [POINTS_KEY]: pointsDifference }
    : {};
  return {
    fiat: subtractFiatPrice(price1.fiat, price2.fiat),
    accountSpecific,
  };
};

export const dividePrice = (price: Prices, divisor: number): Prices => {
  const pointsNewValue = divideAccountSpecificPrice(price, divisor, POINTS_KEY);
  const accountSpecific = pointsNewValue
    ? { [POINTS_KEY]: pointsNewValue }
    : {};
  return {
    fiat: {
      ...price.fiat,
      value: roundPriceToScale(price.fiat.value / divisor),
    },
    accountSpecific,
  };
};

export type PriceBreakdownTaxesAndFees = {
  rateTaxesAndFees?: FiatPrice;
  mandatoryFee?: FiatPrice;
  resortFee?: FiatPrice;
  mandatoryTax?: FiatPrice;
};

export type PriceBreakdown = {
  // Room price per night without discounts and taxes
  perNightSellRate: Prices;
  // Room price for the whole stay without discounts and taxes
  sellRate: Prices;
  // "Strike-Through" Sell Rate before discounts
  sellRateBeforeDiscounts?: Prices;
  // The total amount the user has to pay now: generally it is the sum of the payNowSubTotal and the taxesAndFees
  payNowTotal: Prices;
  // Updated Taxes and Fees, meant to replace feeBreakdown in the future
  taxesAndFees: PriceBreakdownTaxesAndFees;
  // The total amount the user has to pay at the hotel
  payLaterTotal?: FiatPrice;
  // The total amount the user has to pay at the hotel in the hotel's currency
  payLaterTotalLocalCurrency?: FiatPrice;
  // The total amount the user will spend in the trip. It includes pay now + pay later
  tripTotal: FiatPrice;
  // Total pay now amount after discounting points and wallet credit
  amountDue?: FiatPrice;
  // The number of points applied to the booking
  pointsApplied?: number;
  // The offer applied to the booking, if any
  offerApplied?: FiatPrice;
  // Whether the user has to pay the total amount at the hotel
  isPayLater: boolean;
  // Total pay now amount after discounting points and wallet credit
  payNowTotalAfterPoints?: FiatPrice;
};

export const getPriceBreakdownFromQuotedPricing = (
  hotelPricing: PricingV1
): PriceBreakdown => {
  const payNowTotal =
    hotelPricing.paymentSchedule.payNowTotal ??
    pricesZero(hotelPricing.subTotal.fiat);

  const isPayLater = payNowTotal.fiat.value === 0;

  const taxesAndFees: PriceBreakdownTaxesAndFees = {
    mandatoryFee: hotelPricing.taxesAndFeesDetails.breakdown?.fees.mandatoryFee,
    resortFee: hotelPricing.taxesAndFeesDetails.breakdown?.fees.resortFees,
    mandatoryTax:
      hotelPricing.taxesAndFeesDetails.breakdown?.taxes.mandatoryTax?.fiat,
    rateTaxesAndFees:
      hotelPricing.taxesAndFeesDetails.breakdown?.taxes.baseRateTax?.fiat,
  };

  // We ignore the sellRate sent by the Quote PricingV1 here, to prioritize making each price component of
  // the breakdown consistent with actual final price (payNowTotal) paid. We do this by back-calculating the
  // sellRate by subtracting the payNow taxes from the payNowTotal.
  // At most this should fudge the real sellRate by a few cents, which is acceptable because the customer
  // cares more about the breakdown we show being consistent, and reflecting the real price to pay, than
  // they care about the actual sellRate.
  //
  // Ideally we would do this on the back-end, but the B2B PurchaseCoordinator currently doesn't support
  // per-tenant pricing logic.

  const maybePayNowTaxes = getPayNowTaxes(hotelPricing);
  const sellRate = hotelPricing.paymentSchedule.payNowTotal
    ? maybePayNowTaxes
      ? subtractPrice(
          hotelPricing.paymentSchedule.payNowTotal,
          maybePayNowTaxes
        )
      : hotelPricing.paymentSchedule.payNowTotal
    : hotelPricing.subTotal;

  const discount = hotelPricing.discounts?.[0];
  const sellRateBeforeDiscounts =
    discount != null ? subtractPrice(sellRate, discount.value) : undefined;

  // numberOfNights = 0 should never happen, but in case it ever does we'll use 1 to avoid division by zero
  const numberOfNights = Math.max(hotelPricing.stayNights, 1);
  const perNightSellRate = dividePrice(sellRate, numberOfNights);

  const tripTotal = hotelPricing.tripTotal.fiat;

  return {
    perNightSellRate,
    sellRate,
    sellRateBeforeDiscounts,
    payNowTotal,
    payLaterTotal: hotelPricing.paymentSchedule.payLaterTotal?.fiat,
    payLaterTotalLocalCurrency:
      hotelPricing.paymentSchedule.payLaterTotalLocalCurrency?.fiat,
    tripTotal,
    taxesAndFees,
    amountDue: hotelPricing.paymentSchedule.payNowTotal?.fiat,
    isPayLater,
  };
};

export const getPriceBreakdownForRoom = (room: RoomProduct): PriceBreakdown => {
  let payNowTotal: Prices;
  let payLaterTotal: FiatPrice | undefined = undefined;
  let amountDue: FiatPrice | undefined = undefined;
  const isPayLater = room.roomPaymentSchedule === "PayLater";
  if (isPayLater) {
    payLaterTotal = room.tripTotal.fiat;
    payNowTotal = pricesZero(room.tripTotal.fiat);
  } else {
    payLaterTotal = room.feeBreakdown.total;
    payNowTotal = room.total;
    amountDue = room.total.fiat;
  }

  const sellRateBeforeDiscounts =
    room.totalDiscountAware.priceWithNoDiscounts.fiat.value !==
    room.totalDiscountAware.priceWithAllDiscounts.fiat.value
      ? room.totalDiscountAware.priceWithNoDiscounts
      : undefined;

  const taxesAndFees: PriceBreakdownTaxesAndFees = {
    mandatoryFee: room.feeBreakdown.mandatoryFee,
    resortFee: room.feeBreakdown.resortFees,
    mandatoryTax: room.taxBreakdown.mandatoryTax?.fiat,
    rateTaxesAndFees: room.taxBreakdown.baseRateTax?.fiat,
  };

  return {
    perNightSellRate: room.perNightSellRate,
    sellRate: room.sellRate,
    sellRateBeforeDiscounts,
    payLaterTotalLocalCurrency: room.feeBreakdownLocalCurrency?.total,
    payNowTotal: payNowTotal,
    taxesAndFees,
    payLaterTotal: payLaterTotal,
    tripTotal: room.tripTotal.fiat,
    amountDue: amountDue,
    isPayLater,
  };
};

const isPricingPayLater = ({ payNowTotal }: { payNowTotal: Prices }) => {
  return payNowTotal.fiat.value === 0;
};
export const getPriceBreakdownFromReservation = (
  pricing: Pricing,
  payments: PaymentLineItem[] | PaymentLineItemValue[],
  numberOfNights: number
): PriceBreakdown => {
  const isPayLater = isPricingPayLater(pricing);

  const sellRate = pricing.rooms;

  // numberOfNights = 0 should never happen, but in case it ever does we'll use 1 to avoid division by zero
  const perNightSellRate = dividePrice(sellRate, Math.max(1, numberOfNights));

  // Extract loyalty points payment info using shared helper
  const loyaltyPointsInfo = extractLoyaltyPointsPayment(payments);
  const pointsAppliedAmount = loyaltyPointsInfo.pointsAmount;
  const pointsFiatAmount = loyaltyPointsInfo.fiatAmount;

  const tripTotalAfterPoints =
    subtractFiatAndLodgingAmount(pricing.tripTotal.fiat, pointsFiatAmount) ??
    pricing.tripTotal.fiat;

  const payNowTotalAfterPoints =
    subtractFiatAndLodgingAmount(pricing.payNowTotal.fiat, pointsFiatAmount) ??
    pricing.payNowTotal.fiat;

  const payLaterTotalLocalCurrency = isPayLater
    ? pricing.tripTotalInLocalCurrency.fiat
    : pricing.feeBreakdown.total;

  const taxesAndFees = {
    mandatoryTax: pricing.taxBreakdown.mandatoryTax?.fiat,
    mandatoryFee: pricing.feeBreakdown.mandatoryFee,
    resortFee: pricing.feeBreakdown.resortFees,
    rateTaxesAndFees: pricing.taxBreakdown.baseRateTax?.fiat,
  };

  const payLaterTaxesAndFeesFiat = subtractFiatPrice(
    pricing.tripTotal.fiat,
    pricing.payNowTotal.fiat
  );

  const payLaterTotal = isPayLater
    ? pricing.tripTotal.fiat
    : payLaterTaxesAndFeesFiat;

  return {
    perNightSellRate: perNightSellRate,
    sellRate: sellRate,
    payNowTotal: pricing.payNowTotal,
    payLaterTotal: payLaterTotal,
    payLaterTotalLocalCurrency: payLaterTotalLocalCurrency,
    tripTotal: pricing.tripTotal.fiat,
    amountDue: tripTotalAfterPoints,
    payNowTotalAfterPoints,
    pointsApplied: pointsAppliedAmount,
    taxesAndFees: taxesAndFees,
    isPayLater,
  };
};

export const getPriceValueFromString = (price: string) => {
  return Number(price.replace(/[^0-9.-]+/g, ""));
};

export const getPayNowTaxes = (hotelPricing: PricingV1): Prices => {
  return (
    hotelPricing.taxesAndFeesDetails?.payNow ?? {
      fiat: fiatPriceZero(hotelPricing.subTotal.fiat),
      accountSpecific: {},
    }
  );
};
